import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, resolveMediaUrl } from '@/lib/api';
import { ManualEntryImagesField } from '@/components/ManualEntryImagesField';
import { useSession } from '@/hooks/useSession';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { Modal } from '@/components/ui/Modal';
import {
  inputClass,
  labelClass,
  tableWrapClass,
  tableClass,
  theadClass,
  thClass,
  toastBarClass,
  settingsPanelClass,
} from '@/components/ui/inputStyles';
import { draftWechatSummary } from '@/lib/draftStatusUi';
import { formatEntryRelocateMessage, type EntryRelocateResponse } from '@/lib/entryRelocateMessage';
import {
  EntryReviewAlert,
  entryDraftErrmsgForDisplay,
  type EntryReviewReason,
} from '@/lib/entryReviewUi';

type Row = {
  id: number;
  entry_source?: string;
  serial_number: string | null;
  title_text: string | null;
  body_text: string | null;
  contact_phone: string | null;
  valid_start_date: string;
  valid_end_date: string;
  draft_status: string;
  wechat_draft_media_id?: string | null;
  created_at: string;
};

type ListResp = {
  ok: boolean;
  items: Row[];
  total: number;
  page: number;
  per_page: number;
  wechat_draft_ready?: boolean;
};

type ImageRow = {
  id: number;
  source_url: string;
  local_path: string;
  sort_order: number;
};

type EntryDetail = {
  id: number;
  site_id?: number;
  entry_source?: string;
  form_code: string | null;
  form_name: string | null;
  serial_number: string | null;
  title_text: string | null;
  body_text: string | null;
  contact_phone: string | null;
  valid_days?: number;
  valid_start_date: string;
  valid_end_date: string;
  creator_name: string | null;
  weixin_nickname: string | null;
  draft_status: string;
  draft_errmsg: string | null;
  review_reason?: EntryReviewReason;
  matched_sensitive_words?: string[];
  wechat_media_id?: string | null;
  wechat_draft_media_id?: string | null;
  raw_entry_json?: unknown;
  raw_payload?: Record<string, unknown> | null;
  created_at: string;
  updated_at?: string;
  images: ImageRow[];
};

type GetEntryResp = { ok: boolean; entry?: EntryDetail; err?: string; wechat_draft_ready?: boolean };

type RefetchImagesResp = {
  ok: boolean;
  err?: string;
  detail?: string;
  upload_dir?: string;
  updated?: { id: number; local_path: string }[];
  failed?: { id: number; err: string }[];
  skipped_already_saved?: number;
};

const PER_PAGE = 20;

/** 信息登记「手动添加」单图上传上限（与 api upload_manual_entry_image 一致） */
const DRAFT_PUSH_DISABLED_TITLE =
  '请先在「微信公众号」完成一种接入方式：第三方平台扫码绑定公众号，或填写公众号 AppID + AppSecret';

/** 详情弹窗底部：待审核时的操作说明（原因说明见弹窗顶部 EntryReviewAlert） */
function DetailDraftActionsHint({
  draftStatus,
  reviewReason,
  entrySource,
}: {
  draftStatus: string;
  reviewReason?: EntryReviewReason;
  entrySource?: string;
}) {
  const isMpUser = entrySource === 'mp_user';
  if (draftStatus === 'awaiting_review') {
    if (isMpUser || reviewReason === 'mp_user') {
      return (
        <p className="text-xs leading-relaxed text-slate-600">
          本条为小程序用户投稿。请核对内容后点 <strong className="text-slate-800">「通过」</strong> 在小程序公开展示，或点
          <strong className="text-slate-800">「拒绝」</strong> 不予展示（无需推公众号草稿箱）。
        </p>
      );
    }
    return (
      <p className="text-xs leading-relaxed text-slate-600">
        {reviewReason === 'sensitive' ? (
          <>
            改稿请点 <strong className="text-slate-800">「编辑内容」</strong> 并保存；无误后点
            <strong className="text-slate-800">「通过」</strong> 推草稿，不应发布则点
            <strong className="text-slate-800">「拒绝」</strong>。
          </>
        ) : (
          <>
            请核对内容，必要时点 <strong className="text-slate-800">「编辑内容」</strong>；确认可发布后点
            <strong className="text-slate-800">「通过」</strong> 写入草稿箱，否则点
            <strong className="text-slate-800">「拒绝」</strong>。
          </>
        )}
      </p>
    );
  }
  if (draftStatus === 'review_rejected') {
    return (
      <p className="text-xs leading-relaxed text-slate-600">
        本条已 <strong className="text-slate-800">审核拒绝</strong>。若需发布，请编辑内容后点「推送到公众号草稿箱」重试；或删除本条。
      </p>
    );
  }
  if (draftStatus === 'pending') {
    return (
      <p className="text-xs leading-relaxed text-slate-600">
        状态为 <strong className="text-slate-800">待处理</strong>：多为草稿尚未写入或图片未保存到本机。请确认已绑定公众号；若图片显示未落盘可先点「重新下载缺失图片」后再推送。
      </p>
    );
  }
  if (draftStatus === 'skipped') {
    return (
      <p className="text-xs leading-relaxed text-slate-600">
        未自动写草稿。请查看上方「草稿说明」列（常因无本地图片、关关闭自动推稿等），处理后再试推送。
      </p>
    );
  }
  if (draftStatus === 'failed') {
    return (
      <p className="text-xs leading-relaxed text-slate-600">上次推草稿失败。请见「草稿说明」或编辑正文后重试。</p>
    );
  }
  if (draftStatus === 'success') {
    return (
      <p className="text-xs leading-relaxed text-slate-600">本条已写入公众号草稿，请到公众平台选稿发布。通常无需再次推送。</p>
    );
  }
  return (
    <p className="text-xs leading-relaxed text-slate-600">核对内容后，可推送到公众号草稿箱（需已绑定公众号）。</p>
  );
}

function detailPushDraftCtaLabel(draftStatus: string): string {
  if (draftStatus === 'success') {
    return '已在草稿箱';
  }
  return '推送到公众号草稿箱';
}

function entrySourceLabel(src: string | undefined): string {
  if (src === 'manual') return '后台添加';
  if (src === 'mp_user') return '小程序用户';
  return '金数据';
}

function isMpUserEntry(src: string | undefined): boolean {
  return src === 'mp_user';
}

function DetailRow({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
}) {
  const v = value == null || value === '' ? '—' : value;
  return (
    <div className="grid gap-1 border-b border-slate-100/90 py-2.5 text-sm last:border-0 sm:grid-cols-[minmax(7rem,120px)_1fr] sm:gap-4">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div
        className={
          multiline && v !== '—'
            ? 'break-words whitespace-pre-wrap text-slate-900'
            : 'break-words text-slate-900'
        }
      >
        {v}
      </div>
    </div>
  );
}

type SiteListItem = { id: number; site_name: string; enabled: boolean };

export function EntriesPage() {
  const { currentSiteId, currentSiteName } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [wechatDraftReady, setWechatDraftReady] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<EntryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [refetchImgBusy, setRefetchImgBusy] = useState(false);
  const [refetchImgMsg, setRefetchImgMsg] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<Row | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editImagePaths, setEditImagePaths] = useState<string[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const [actionBusyId, setActionBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addBody, setAddBody] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addImagePaths, setAddImagePaths] = useState<string[]>([]);
  const [addSaving, setAddSaving] = useState(false);

  /** 可切换的站点（多于 1 个时在列表中提供「换站点」） */
  const [accessibleSites, setAccessibleSites] = useState<SiteListItem[]>([]);
  const prevSiteIdRef = useRef(0);

  const detailHasMissingImages = useMemo(() => {
    if (!detail?.images?.length) return false;
    return detail.images.some((im) => !im.local_path?.trim());
  }, [detail]);

  const load = useCallback(async (opts?: { page?: number }) => {
    const p = opts?.page ?? page;
    setLoading(true);
    setErr(null);
    try {
      const r = await apiFetch<ListResp>('list_entries', {
        method: 'GET',
        query: {
          page: p,
          per_page: PER_PAGE,
          ...(appliedQ.trim() !== '' ? { q: appliedQ.trim() } : {}),
        },
      });
      if (r.ok) {
        setPage(p);
        setItems(r.items);
        setTotal(r.total);
        setWechatDraftReady(Boolean(r.wechat_draft_ready));
      } else {
        setErr('加载失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        setErr(null);
        return;
      }
      setErr(e instanceof Error ? e.message : '错误');
    } finally {
      setLoading(false);
    }
  }, [page, appliedQ]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadAccessibleSites = useCallback(async () => {
    try {
      const r = await apiFetch<{
        ok: boolean;
        items?: { id: number; site_name: string; enabled: boolean }[];
      }>('list_sites', { method: 'GET', query: { page: 1, per_page: 100 } });
      if (r.ok && Array.isArray(r.items)) {
        setAccessibleSites(r.items.filter((s) => s.enabled !== false));
      } else {
        setAccessibleSites([]);
      }
    } catch {
      setAccessibleSites([]);
    }
  }, []);

  useEffect(() => {
    void loadAccessibleSites();
  }, [loadAccessibleSites, currentSiteId]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    setDetailErr(null);
    setDetail(null);
    try {
      const r = await apiFetch<GetEntryResp>('get_entry', {
        method: 'GET',
        query: { id },
      });
      if (r.ok && r.entry) {
        setDetail(r.entry);
        if (typeof r.wechat_draft_ready === 'boolean') {
          setWechatDraftReady(r.wechat_draft_ready);
        }
      } else {
        setDetailErr(r.err || '加载失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setDetailErr(e instanceof Error ? e.message : '错误');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  function openDetail(id: number) {
    setDetailId(id);
    setDetailOpen(true);
    void loadDetail(id);
  }

  useEffect(() => {
    if (!reviewParam) return;
    const id = parseInt(reviewParam, 10);
    if (!Number.isFinite(id) || id < 1) return;
    setDetailId(id);
    setDetailOpen(true);
    void loadDetail(id);
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
  }, [reviewParam, loadDetail, searchParams, setSearchParams]);

  function closeDetail() {
    setDetailOpen(false);
    setDetailId(null);
    setDetail(null);
    setDetailErr(null);
    setRefetchImgMsg('');
  }

  /** 顶栏切换工作站点后：重载列表并刷新当前站点的公众号草稿可用状态（避免「通过」仍沿用上一站点配置） */
  useEffect(() => {
    if (currentSiteId < 1) return;
    const prev = prevSiteIdRef.current;
    prevSiteIdRef.current = currentSiteId;
    if (prev !== 0 && prev !== currentSiteId) {
      setPage(1);
      closeDetail();
      void load({ page: 1 });
    }
  }, [currentSiteId, load]);

  async function openEdit(r: Row) {
    setEditRow(r);
    setEditTitle(r.title_text ?? '');
    setEditBody(r.body_text ?? '');
    setEditPhone(r.contact_phone ?? '');
    setEditEndDate(r.valid_end_date?.slice(0, 10) ?? '');
    setEditImagePaths([]);
    setEditOpen(true);
    setEditLoading(true);
    try {
      const res = await apiFetch<GetEntryResp>('get_entry', {
        method: 'GET',
        query: { id: r.id, site_id: currentSiteId },
      });
      if (res.ok && res.entry?.images) {
        setEditImagePaths(
          res.entry.images
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((im) => im.local_path?.trim() ?? '')
            .filter(Boolean)
        );
      }
    } catch {
      setToast('加载图片失败，仍可编辑文字后保存');
    } finally {
      setEditLoading(false);
    }
  }

  function closeEdit() {
    setEditOpen(false);
    setEditRow(null);
  }

  function openEditFromDetail() {
    if (!detail) {
      return;
    }
    openEdit({
      id: detail.id,
      entry_source: detail.entry_source,
      serial_number: detail.serial_number,
      title_text: detail.title_text,
      body_text: detail.body_text,
      contact_phone: detail.contact_phone,
      valid_start_date: detail.valid_start_date,
      valid_end_date: detail.valid_end_date,
      draft_status: detail.draft_status,
      created_at: detail.created_at,
    });
    closeDetail();
  }

  const saveEdit = async () => {
    if (!editRow) return;
    setEditSaving(true);
    setToast(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('update_entry', {
        method: 'POST',
        body: JSON.stringify({
          entry_id: editRow.id,
          title_text: editTitle,
          body_text: editBody,
          contact_phone: editPhone,
          valid_end_date: editEndDate,
          image_local_paths: editImagePaths,
        }),
      });
      if (r.ok) {
        setToast('已保存');
        closeEdit();
        await load();
        if (detailOpen && detailId === editRow.id) {
          await loadDetail(editRow.id);
        }
      } else {
        setToast(r.err === 'valid_end_before_start' ? '结束日期不能早于开始日期' : '保存失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '保存失败');
    } finally {
      setEditSaving(false);
    }
  };

  const moveEntrySite = async (entryId: number, fromSiteId: number, newSiteId: number) => {
    if (newSiteId < 1 || newSiteId === fromSiteId) return;
    const target = accessibleSites.find((s) => s.id === newSiteId);
    const targetLabel = target?.site_name ?? `站点 #${newSiteId}`;
    if (
      !window.confirm(
        `确定将信息 #${entryId} 从「${currentSiteName || `站点 #${fromSiteId}`}」迁入「${targetLabel}」？\n将在目标站创建新信息，并删除原信息及其转发任务。`
      )
    ) {
      return;
    }
    setActionBusyId(entryId);
    setToast(null);
    try {
      const r = await apiFetch<EntryRelocateResponse>('move_entry_site', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId, new_site_id: newSiteId }),
      });
      if (r.ok) {
        setItems((prev) => prev.filter((x) => x.id !== entryId));
        setToast(formatEntryRelocateMessage(r, targetLabel));
        await load();
        if (detailOpen && detailId === entryId) closeDetail();
      } else {
        const hint =
          r.err === 'dedupe_conflict'
            ? r.detail || '目标站点已有相同金数据幂等键，无法迁入'
            : r.err === 'forbidden_site'
              ? '无权操作该站点'
              : r.detail || r.err || '更换站点失败';
        setToast(hint);
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '更换站点失败');
    } finally {
      setActionBusyId(null);
    }
  };

  const deleteEntry = async (id: number) => {
    if (!window.confirm(`确定删除信息 #${id}？删除后不可恢复。`)) return;
    setActionBusyId(id);
    setToast(null);
    try {
      const r = await apiFetch<{ ok: boolean }>('delete_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: id }),
      });
      if (r.ok) {
        setToast('已删除');
        if (detailOpen && detailId === id) closeDetail();
        await load();
      } else {
        setToast('删除失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '删除失败');
    } finally {
      setActionBusyId(null);
    }
  };

  const pushDraft = async (id: number) => {
    setActionBusyId(id);
    setToast(null);
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        draft_status?: string;
        matched_sensitive_words?: string[];
      }>('push_entry_draft', {
        method: 'POST',
        body: JSON.stringify({ entry_id: id }),
      });
      if (r.ok) {
        const st = r.draft_status ?? '';
        const d = r.detail ?? '';
        setToast(
          st === 'success'
            ? '已提交到微信草稿箱'
            : st === 'skipped'
              ? `未写入草稿（${d || 'skipped'}）`
              : st === 'failed'
                ? `草稿失败：${d}`
                : d || '已完成请求',
        );
        await load();
        if (detailOpen && detailId === id) {
          await loadDetail(id);
        }
      } else if (r.err === 'sensitive_blocked') {
        setToast(r.detail || '仍含敏感词，请先修改并保存');
        if (detailOpen && detailId === id) {
          setDetail((prev) =>
            prev && prev.id === id
              ? {
                  ...prev,
                  review_reason: 'sensitive',
                  matched_sensitive_words: r.matched_sensitive_words ?? prev.matched_sensitive_words,
                }
              : prev,
          );
        }
      } else {
        setToast(r.detail || r.err || '提交失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '提交失败');
    } finally {
      setActionBusyId(null);
    }
  };

  const rejectEntryReview = async (id: number) => {
    if (!window.confirm('确定拒绝本条？将标记为「审核已拒绝」，不会写入公众号草稿箱。')) {
      return;
    }
    setActionBusyId(id);
    setToast(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('reject_entry_review', {
        method: 'POST',
        body: JSON.stringify({ entry_id: id }),
      });
      if (r.ok) {
        setToast('已拒绝本条');
        await load();
        if (detailOpen && detailId === id) {
          await loadDetail(id);
        }
      } else {
        setToast(r.detail || r.err || '操作失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '操作失败');
    } finally {
      setActionBusyId(null);
    }
  };

  const approveMpUserEntry = async (id: number, siteId?: number) => {
    if (!window.confirm('确定通过审核？通过后将在小程序公开展示。')) {
      return;
    }
    setActionBusyId(id);
    setToast(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string; draft_status?: string }>(
        'approve_mp_user_entry',
        {
          method: 'POST',
          body: JSON.stringify({ entry_id: id, site_id: siteId }),
        },
      );
      if (r.ok) {
        setToast('已通过审核，小程序端可展示');
        await load();
        if (detailOpen && detailId === id) {
          await loadDetail(id);
        }
      } else {
        setToast(r.detail || r.err || '审核失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '审核失败');
    } finally {
      setActionBusyId(null);
    }
  };

  const refetchMissingImages = useCallback(async () => {
    if (!detailId || detailId < 1) return;
    setRefetchImgBusy(true);
    setRefetchImgMsg('');
    try {
      const r = await apiFetch<RefetchImagesResp>('refetch_entry_images', {
        method: 'POST',
        body: JSON.stringify({ entry_id: detailId, only_missing: true }),
      });
      if (r.ok) {
        const n = r.updated?.length ?? 0;
        const f = r.failed?.length ?? 0;
        const sk = r.skipped_already_saved ?? 0;
        if (n === 0 && f === 0) {
          setRefetchImgMsg(sk > 0 ? `无需补下载：${sk} 张已有本地路径` : '没有图片记录可处理');
        } else {
          setRefetchImgMsg(
            f > 0
              ? `已写入 ${n} 张；${f} 张仍失败（请查服务器 error_log）；已跳过已有本地路径 ${sk} 张`
              : `已写入 ${n} 张本地路径；已跳过 ${sk} 张（本已有文件）`
          );
        }
        await loadDetail(detailId);
      } else {
        const hint =
          r.err === 'upload_dir_denied'
            ? `${r.detail || 'upload_dir 无写权限'}（路径：${r.upload_dir || '—'}）`
            : r.detail || r.err || '操作失败';
        setRefetchImgMsg(hint);
      }
    } catch (e) {
      setRefetchImgMsg(e instanceof Error ? e.message : '请求失败');
    } finally {
      setRefetchImgBusy(false);
    }
  }, [detailId, loadDetail]);

  function runSearch() {
    setAppliedQ(qInput.trim());
    setPage(1);
  }

  function openAddModal() {
    setAddTitle('');
    setAddBody('');
    setAddPhone('');
    setAddImagePaths([]);
    setAddOpen(true);
  }

  async function submitAddEntry() {
    const t = addTitle.trim();
    const b = addBody.trim();
    if (t === '' && b === '') {
      setToast('请填写标题或正文');
      return;
    }
    setAddSaving(true);
    setToast(null);
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        entry_id?: number;
        draft_status?: string;
        draft_detail?: string;
      }>('create_manual_entry', {
        method: 'POST',
        body: JSON.stringify({
          title_text: t,
          body_text: b,
          contact_phone: addPhone.trim(),
          image_local_paths: addImagePaths.length > 0 ? addImagePaths : undefined,
        }),
      });
      if (r.ok) {
        setAddOpen(false);
        const st = r.draft_status ?? '';
        const d = r.draft_detail ?? '';
        setToast(
          st === 'success'
            ? '已添加并已提交到公众号草稿箱'
            : st === 'awaiting_review'
              ? '已添加，待人工审核后再进草稿箱'
              : st === 'skipped' || st === 'failed'
                ? `已添加（草稿：${d || st}）`
                : '已添加',
        );
        await load({ page: 1 });
      } else {
        setToast(r.detail || r.err || '添加失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '添加失败');
    } finally {
      setAddSaving(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="space-y-6">
      <PageHeader
        title="信息登记"
        description="支持金数据自动入库与后台手动添加。未进草稿的条目在列表或详情中可「推送到公众号草稿箱」（需先在「微信公众号」完成授权）。需人工把关时请在「接入与推送」开启审核或配置敏感词；详情底部有操作说明。贴图需至少一张已成功落盘的图片。"
      />

      {toast ? <div className={toastBarClass}>{toast}</div> : null}

      <Card title="筛选">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className={labelClass + ' flex-1'}>
            <span>关键词（标题 / 正文 / 手机 / 编号）</span>
            <input
              className={inputClass}
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
            />
          </label>
          <Button type="button" variant="primary" onClick={runSearch}>
            搜索
          </Button>
        </div>
      </Card>

      {loading ? (
        <Spinner label="加载中" />
      ) : err ? (
        <EmptyState icon={<i className="fa-solid fa-triangle-exclamation text-2xl" aria-hidden />}>{err}</EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>暂无数据</EmptyState>
      ) : (
        <Card
          title="列表"
          titleExtra={
            <Button type="button" variant="primary" size="sm" onClick={openAddModal}>
              添加登记
            </Button>
          }
        >
          <div className={tableWrapClass}>
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>ID</th>
                  <th className={thClass}>站点</th>
                  <th className={thClass}>来源</th>
                  <th className={thClass}>编号</th>
                  <th className={thClass}>标题</th>
                  <th className={thClass}>有效期至</th>
                  <th className={thClass}>公众号草稿</th>
                  <th className={thClass}>创建时间</th>
                  <th className={thClass}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const dw = draftWechatSummary(r.draft_status, {
                    wechatDraftMediaId: r.wechat_draft_media_id,
                    entrySource: r.entry_source,
                  });
                  const manual = r.entry_source === 'manual';
                  return (
                    <tr
                      key={r.id}
                      className={
                        'border-t border-slate-100 text-sm text-slate-800' +
                        (manual ? ' bg-sky-50/50' : '') +
                        (dw.warn ? ' border-l-[3px] border-l-amber-400' : '')
                      }
                    >
                      <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                      <td className="min-w-[140px] px-3 py-2">
                        {accessibleSites.length > 1 ? (
                          <select
                            className={
                              inputClass +
                              ' max-w-[200px] py-1 text-xs' +
                              (actionBusyId === r.id ? ' opacity-60' : '')
                            }
                            value={currentSiteId}
                            disabled={actionBusyId === r.id || currentSiteId < 1}
                            title="将本条信息迁移到所选站点（须为您有权限的站点）"
                            onChange={(e) => {
                              const next = Number(e.target.value);
                              if (!Number.isFinite(next) || next < 1 || next === currentSiteId) return;
                              void moveEntrySite(r.id, currentSiteId, next);
                            }}
                          >
                            {accessibleSites.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.site_name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-slate-600">{currentSiteName || '—'}</span>
                        )}
                        {actionBusyId === r.id ? (
                          <span className="mt-0.5 block text-[10px] font-medium text-sky-700">正在更换站点…</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            manual
                              ? 'inline-block rounded-md bg-sky-100 px-1.5 py-0.5 text-[11px] font-medium text-sky-900'
                              : 'text-xs text-slate-500'
                          }
                        >
                          {entrySourceLabel(r.entry_source)}
                        </span>
                      </td>
                      <td className="px-3 py-2">{r.serial_number || '—'}</td>
                      <td className="max-w-[200px] truncate px-3 py-2">{r.title_text || r.body_text || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs">{r.valid_end_date}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={dw.warn ? 'font-medium text-amber-900' : 'font-medium text-emerald-800'}>
                          {dw.main}
                        </span>
                        {dw.sub ? (
                          <span className="mt-0.5 block text-[11px] text-slate-500">{dw.sub}</span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">{r.created_at}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <button
                            type="button"
                            className="text-sm font-medium text-teal-700 hover:underline disabled:opacity-50"
                            disabled={actionBusyId === r.id}
                            onClick={() => void openEdit(r)}
                          >
                            编辑
                          </button>
                          <button
                            type="button"
                            className="text-sm font-medium text-rose-700 hover:underline disabled:opacity-50"
                            disabled={actionBusyId === r.id}
                            onClick={() => void deleteEntry(r.id)}
                          >
                            删除
                          </button>
                          {r.draft_status === 'awaiting_review' ? (
                            <button
                              type="button"
                              className="text-sm font-semibold text-teal-800 hover:underline"
                              disabled={actionBusyId === r.id}
                              onClick={() => openDetail(r.id)}
                            >
                              {actionBusyId === r.id ? '处理中…' : '审核'}
                            </button>
                          ) : (
                            <>
                              <span className="inline-flex">
                                <button
                                  type="button"
                                  disabled={!wechatDraftReady || actionBusyId === r.id}
                                  title={!wechatDraftReady ? DRAFT_PUSH_DISABLED_TITLE : undefined}
                                  className={`text-sm font-medium disabled:cursor-not-allowed ${
                                    wechatDraftReady
                                      ? 'text-indigo-700 hover:underline disabled:opacity-50'
                                      : 'text-slate-400'
                                  }`}
                                  onClick={() => wechatDraftReady && void pushDraft(r.id)}
                                >
                                  {actionBusyId === r.id ? '处理中…' : '上传公众号草稿箱'}
                                </button>
                              </span>
                              <button
                                type="button"
                                className="text-sm font-medium text-slate-600 hover:underline"
                                onClick={() => openDetail(r.id)}
                              >
                                详情
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            summary={total > 0 ? `共 ${total} 条，每页 ${PER_PAGE} 条` : undefined}
            className="mt-4"
          />
        </Card>
      )}

      <Modal
        open={addOpen}
        title="添加登记"
        onClose={() => !addSaving && setAddOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" disabled={addSaving} onClick={() => setAddOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={addSaving || (addTitle.trim() === '' && addBody.trim() === '')}
              onClick={() => void submitAddEntry()}
            >
              {addSaving ? '保存中…' : '确定添加'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            添加后若站点在「接入与推送」已开启入库推草稿，将自动写入公众号草稿箱（与金数据入库规则一致）；否则为未进草稿箱，可在列表手动上传。登记编号与展示期限由系统自动生成；填写人记为「手动添加」。贴图模式需至少一张已成功落盘的图片。
          </p>
          <label className={labelClass}>
            <span>标题（可选）</span>
            <input className={inputClass} value={addTitle} onChange={(e) => setAddTitle(e.target.value)} placeholder="简短标题" />
          </label>
          <label className={labelClass}>
            <span>正文</span>
            <textarea
              className={inputClass + ' min-h-[100px]'}
              value={addBody}
              onChange={(e) => setAddBody(e.target.value)}
              placeholder="登记正文，可与标题二选一必填一项"
            />
          </label>
          <label className={labelClass}>
            <span>联系电话（可选）</span>
            <input className={inputClass} value={addPhone} onChange={(e) => setAddPhone(e.target.value)} />
          </label>
          <ManualEntryImagesField
            siteId={currentSiteId}
            paths={addImagePaths}
            onChange={setAddImagePaths}
            disabled={addSaving}
            onError={(m) => setToast(m)}
          />
        </div>
      </Modal>

      <Modal open={editOpen} onClose={() => !editSaving && closeEdit()} title={editRow ? `编辑信息 #${editRow.id}` : '编辑'}>
        {editRow ? (
          <div className="space-y-4">
            <label className={labelClass}>
              <span>标题</span>
              <input className={inputClass} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </label>
            <label className={labelClass}>
              <span>正文</span>
              <textarea
                className={inputClass + ' min-h-[120px]'}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
              />
            </label>
            <label className={labelClass}>
              <span>联系电话</span>
              <input className={inputClass} value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
            </label>
            <label className={labelClass}>
              <span>有效期至</span>
              <input
                type="date"
                className={inputClass}
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
              />
            </label>
            <p className="text-xs text-slate-500">展示开始日为入库时的起始日，修改「有效期至」将同步重算有效天数。</p>
            {editLoading ? (
              <p className="text-sm text-slate-500">正在加载图片…</p>
            ) : (
              <ManualEntryImagesField
                siteId={currentSiteId}
                paths={editImagePaths}
                onChange={setEditImagePaths}
                disabled={editSaving}
                onError={(m) => setToast(m)}
              />
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" variant="success" onClick={() => void saveEdit()} disabled={editSaving || editLoading}>
                {editSaving ? '保存中…' : '保存'}
              </Button>
              <Button type="button" variant="secondary" onClick={closeEdit} disabled={editSaving}>
                取消
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={detailOpen}
        onClose={closeDetail}
        wide
        title={detailId ? `信息详情 #${detailId}` : '信息详情'}
        footer={
          !detailLoading && !detailErr && detail ? (
            <div className="space-y-3">
              <DetailDraftActionsHint
                draftStatus={detail.draft_status}
                reviewReason={detail.review_reason}
                entrySource={detail.entry_source}
              />
              <div className="flex flex-wrap items-center justify-end gap-2">
                {detail.draft_status === 'awaiting_review' ? (
                  <>
                    <Button type="button" variant="secondary" onClick={openEditFromDetail}>
                      编辑内容
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      disabled={actionBusyId === detail.id}
                      onClick={() => void rejectEntryReview(detail.id)}
                    >
                      {actionBusyId === detail.id ? '处理中…' : '拒绝'}
                    </Button>
                    {isMpUserEntry(detail.entry_source) || detail.review_reason === 'mp_user' ? (
                      <Button
                        type="button"
                        variant="primary"
                        disabled={actionBusyId === detail.id}
                        onClick={() => void approveMpUserEntry(detail.id, detail.site_id)}
                      >
                        {actionBusyId === detail.id ? '处理中…' : '通过并展示'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        disabled={!wechatDraftReady || actionBusyId === detail.id}
                        title={!wechatDraftReady ? DRAFT_PUSH_DISABLED_TITLE : undefined}
                        onClick={() => void pushDraft(detail.id)}
                      >
                        {actionBusyId === detail.id ? '处理中…' : '通过'}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button type="button" variant="secondary" onClick={openEditFromDetail}>
                      编辑内容
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      className="max-w-full whitespace-normal text-left text-sm leading-snug sm:max-w-none sm:whitespace-nowrap"
                      disabled={
                        !wechatDraftReady || actionBusyId === detail.id || detail.draft_status === 'success'
                      }
                      title={
                        !wechatDraftReady
                          ? DRAFT_PUSH_DISABLED_TITLE
                          : detail.draft_status === 'success'
                            ? '本条已成功写入公众号草稿'
                            : undefined
                      }
                      onClick={() => void pushDraft(detail.id)}
                    >
                      {actionBusyId === detail.id ? '处理中…' : detailPushDraftCtaLabel(detail.draft_status)}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null
        }
      >
        {detailLoading ? (
          <Spinner label="加载详情" />
        ) : detailErr ? (
          <p className="text-sm text-rose-600">{detailErr}</p>
        ) : detail ? (
          <div className="space-y-6">
            <EntryReviewAlert
              draftStatus={detail.draft_status}
              reviewReason={detail.review_reason}
              matchedWords={detail.matched_sensitive_words}
            />
            <section>
              <h3 className="mb-3 text-sm font-semibold tracking-tight text-slate-800">表单与编号</h3>
              <div className={settingsPanelClass}>
                <DetailRow label="数据来源" value={entrySourceLabel(detail.entry_source)} />
                <DetailRow label="表单代码" value={detail.form_code} />
                <DetailRow label="表单名称" value={detail.form_name} />
                <DetailRow label="登记编号" value={detail.serial_number} />
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-sm font-semibold tracking-tight text-slate-800">登记内容</h3>
              <div className={settingsPanelClass}>
                <DetailRow label="标题" value={detail.title_text} />
                <DetailRow label="正文" value={detail.body_text} multiline />
                <DetailRow label="联系电话" value={detail.contact_phone} />
                <DetailRow label="有效期至" value={detail.valid_end_date} />
                <DetailRow label="填写人" value={detail.creator_name} />
                <DetailRow label="微信昵称" value={detail.weixin_nickname} />
                <DetailRow
                  label="公众号草稿"
                  value={(() => {
                    const dw = draftWechatSummary(detail.draft_status, {
                      wechatDraftMediaId: detail.wechat_draft_media_id,
                      entrySource: detail.entry_source,
                    });
                    return dw.sub ? `${dw.main}（${dw.sub}）` : dw.main;
                  })()}
                />
                <DetailRow label="草稿说明" value={entryDraftErrmsgForDisplay(detail)} />
                <DetailRow label="入库时间" value={detail.created_at} />
              </div>
            </section>

            {detail.images && detail.images.length > 0 ? (
              <section>
                <h3 className="mb-3 text-sm font-semibold tracking-tight text-slate-800">图片附件</h3>
                {detailHasMissingImages ? (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <Button type="button" onClick={() => void refetchMissingImages()} disabled={refetchImgBusy}>
                      {refetchImgBusy ? '下载中…' : '重新下载缺失图片到服务器'}
                    </Button>
                    {refetchImgMsg ? <span className="text-xs text-slate-600">{refetchImgMsg}</span> : null}
                  </div>
                ) : null}
                <ul className="flex flex-wrap gap-3">
                  {detail.images.map((im) => {
                    const href = resolveMediaUrl(im.local_path);
                    const saved = Boolean(im.local_path && im.local_path.trim() !== '');
                    return (
                      <li key={im.id} className="max-w-[200px]">
                        <div className="mb-1 text-[10px] font-medium text-slate-600">
                          {saved ? (
                            <span className="text-emerald-700">已落盘</span>
                          ) : (
                            <span className="text-amber-800">未落盘</span>
                          )}
                        </div>
                        {href ? (
                          <a href={href} target="_blank" rel="noreferrer" className="block">
                            <img
                              src={href}
                              alt=""
                              className="max-h-40 rounded-xl border border-slate-200/90 object-contain shadow-sm"
                            />
                          </a>
                        ) : (
                          <div className="rounded-xl border border-dashed border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white/50 px-2 py-6 text-center text-xs text-slate-500">
                            无本地文件
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
